import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse, className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hoveredItem, setHoveredItem] = useState(null);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/ai-agents-dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'AI-Agenten Dashboard',
      description: 'Alle Agenten verwalten'
    },
    {
      label: 'Configuration',
      path: '/agent-configuration',
      icon: 'Settings',
      tooltip: 'Agent Configuration',
      description: 'Agent einrichten'
    },
    {
      label: 'Appearance',
      path: '/appearance-customization',
      icon: 'Palette',
      tooltip: 'Appearance Customization',
      description: 'Design anpassen'
    }
  ];

  const bottomItems = [
    {
      label: 'Settings',
      path: '/settings',
      icon: 'Settings',
      tooltip: 'Settings',
      description: 'Einstellungen'
    },
    {
      label: 'Help',
      path: '/help',
      icon: 'HelpCircle',
      tooltip: 'Help & Support',
      description: 'Hilfe & Support'
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const Logo = () => (
    <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} mb-8`}>
      <div className="w-8 h-8 gradient-primary rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon name="Zap" size={20} color="white" strokeWidth={2.5} />
      </div>
      {!isCollapsed && (
        <span className="text-xl font-semibold text-secondary font-heading">
          Vibecoding
        </span>
      )}
    </div>
  );

  const NavItem = ({ item, isBottom = false }) => {
    const isActive = isActivePath(item?.path);
    const isHovered = hoveredItem === item?.path;

    return (
      <div className="relative">
        <button
          onClick={() => handleNavigation(item?.path)}
          onMouseEnter={() => setHoveredItem(item?.path)}
          onMouseLeave={() => setHoveredItem(null)}
          className={`
            w-full flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200
            ${isActive 
              ? 'bg-primary text-primary-foreground shadow-sm' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }
            ${isCollapsed ? 'justify-center' : ''}
            focus-ring
          `}
          title={isCollapsed ? item?.tooltip : ''}
        >
          <Icon 
            name={item?.icon} 
            size={20} 
            className={`flex-shrink-0 ${isActive ? 'text-primary-foreground' : ''}`}
          />
          {!isCollapsed && (
            <div className="flex-1 text-left">
              <div className="text-sm font-medium">{item?.label}</div>
              <div className={`text-xs ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                {item?.description}
              </div>
            </div>
          )}
        </button>
        {/* Tooltip for collapsed state */}
        {isCollapsed && isHovered && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-popover border border-border rounded-md shadow-lg z-50 animate-scale-in">
            <div className="text-sm font-medium text-popover-foreground">{item?.label}</div>
            <div className="text-xs text-muted-foreground">{item?.description}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`
        hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:flex-col
        ${isCollapsed ? 'lg:w-16' : 'lg:w-60'}
        bg-card border-r border-border transition-all duration-300 z-60
        ${className}
      `}>
        <div className="flex flex-col h-full p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <Logo />
            {!isCollapsed && onToggleCollapse && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleCollapse}
                iconName="PanelLeftClose"
                className="w-8 h-8 p-0"
              />
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2">
            {navigationItems?.map((item) => (
              <NavItem key={item?.path} item={item} />
            ))}
          </nav>

          {/* Bottom Items */}
          <div className="space-y-2 pt-4 border-t border-border">
            {bottomItems?.map((item) => (
              <NavItem key={item?.path} item={item} isBottom />
            ))}
            
            {/* Collapse Toggle */}
            {onToggleCollapse && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleCollapse}
                iconName={isCollapsed ? "PanelLeftOpen" : "PanelLeftClose"}
                className={`w-full ${isCollapsed ? 'justify-center' : 'justify-start'}`}
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {!isCollapsed && 'Collapse'}
              </Button>
            )}
          </div>
        </div>
      </aside>
      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50">
        <div className="grid grid-cols-3 gap-1 p-2">
          {navigationItems?.map((item) => {
            const isActive = isActivePath(item?.path);
            return (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`
                  flex flex-col items-center justify-center py-2 px-1 rounded-lg transition-colors
                  ${isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon 
                  name={item?.icon} 
                  size={20} 
                  className={`mb-1 ${isActive ? 'text-primary-foreground' : ''}`}
                />
                <span className={`text-xs font-medium ${isActive ? 'text-primary-foreground' : ''}`}>
                  {item?.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default Sidebar;